import { useEffect, useState } from "react"
import type { CombinedError } from "urql"
import { useQuery } from "urql"

import type { Location, LocationQueryListArgs, Query } from "@/graphql/codegen/graphql"
import { LocationListDocument } from "@/graphql/codegen/graphql"

export interface UseGetLocationsReturn {
  locations: Location[]
  isLocationsFetching: boolean
  locationsError: CombinedError | undefined
  loadMoreLocations: () => void
  getLocationsWithReset: () => void
  resetLocations: () => void
}

const LIMIT = 20

export const useGetLocations = (
  companyId?: string,
  params: { isLocationActive?: boolean } = {}
): UseGetLocationsReturn => {
  const [locations, setLocations] = useState<Location[]>([])
  const [offset, setOffset] = useState(0)
  const [hasNextPage, setHasNextPage] = useState(true)
  const { isLocationActive } = params

  const [{ data, fetching, error }, reexecuteQuery] = useQuery<Pick<Query, "location">, LocationQueryListArgs>({
    query: LocationListDocument,
    variables: {
      input: { filter: { companyId, ...(isLocationActive && { isLocationActive }) }, limit: LIMIT, offset },
    },
    pause: !companyId,
  })

  useEffect(() => {
    if (data?.location.list.__typename === "LocationListSuccess") {
      const paginatedLocations = data.location.list.locations
      setLocations((prev: Location[]) => (offset === 0 ? paginatedLocations : [...prev, ...paginatedLocations]))
      if (paginatedLocations.length < LIMIT) {
        setHasNextPage(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const loadMoreLocations = () => {
    if (hasNextPage) {
      setOffset((prevOffset) => prevOffset + LIMIT)
    }
  }

  const resetLocations = () => {
    setOffset(0)
    setLocations([])
    setHasNextPage(true)
  }

  const getLocationsWithReset = () => {
    resetLocations()
    reexecuteQuery({ requestPolicy: "network-only" })
  }

  return {
    locations,
    isLocationsFetching: fetching,
    locationsError: error,
    loadMoreLocations,
    getLocationsWithReset,
    resetLocations,
  }
}
